(() => {
  const instances = new WeakMap();

  const normalize = (value) => {
    if (typeof value !== 'string') {
      return '';
    }

    return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();
  };

  const isSafeUrl = (url) => {
    if (typeof url !== 'string' || url.trim() === '') {
      return false;
    }

    if (url.startsWith('/') && url.startsWith('//') === false) {
      return true;
    }

    try {
      const parsed = new URL(url, window.location.origin);

      return parsed.protocol === 'https:' || parsed.protocol === 'http:';
    } catch {
      return false;
    }
  };

  const rankText = (text, query) => {
    const value = normalize(text);
    const needle = normalize(query);

    if (needle === '' || value.includes(needle) === false) {
      return 3;
    }

    if (value === needle) {
      return 0;
    }

    if (value.startsWith(needle)) {
      return 1;
    }

    return 2;
  };

  const decodeEntities = (value) => {
    if (typeof value !== 'string' || value.includes('&') === false) {
      return value;
    }

    const node = document.createElement('textarea');

    node.innerHTML = value;

    return node.value;
  };

  const indexRequests = new Map();

  const fetchHelpJson = (fileName) => {
    const cached = indexRequests.get(fileName);

    if (cached != null) {
      return cached;
    }

    const request = fetch(`/cdn/shop/files/${fileName}?v=${Date.now()}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Help search: failed to fetch ${fileName}`);
        }

        return response.json();
      })
      .catch((error) => {
        indexRequests.delete(fileName);
        throw error;
      });

    indexRequests.set(fileName, request);

    return request;
  };

  const readJson = (root, selector) => {
    const node = root.querySelector(selector);

    if (node == null || node.textContent.trim() === '') {
      return null;
    }

    return JSON.parse(node.textContent);
  };

  class HelpSearchMain {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('[data-help-search-form]');
      this.input = root.querySelector('[data-help-search-input]');
      this.results = root.querySelector('[data-help-search-results]');
      this.heading = root.querySelector('[data-help-search-heading]');
      this.tabs = root.querySelector('[data-help-search-tabs]');
      this.header = root.querySelector('[data-help-search-header]');
      this.activeType = 'category';
      this.currentPage = 1;
      this.pageSize = 10;
      this.fileName = root.dataset.helpFile || 'help.json';
      this.localeRoot = root.dataset.localeRoot || '';
      this.destroyed = false;
      this.index = { categories: [], articles: [] };
      this.labels = {};
      this.minLength = 2;
      this.resultsUrl = this.form == null ? window.location.pathname : this.form.action;

      if (this.form == null || this.input == null || this.results == null || this.tabs == null || this.header == null) {
        return;
      }

      this.#readConfig();
      this.#loadIndex().then(() => {
        if (this.destroyed) {
          return;
        }

        this.#applyQueryFromUrl();
      });
    }

    destroy() {
      this.destroyed = true;
      this.root.removeAttribute('data-help-search-main-ready');
    }

    #readConfig() {
      try {
        const config = readJson(this.root, '[data-help-search-config]');

        if (config != null) {
          this.pageSize = Number(config.pageSize) || 10;
          this.minLength = Number(config.minLength) || 2;
          this.resultsUrl = typeof config.resultsUrl === 'string' ? config.resultsUrl : this.resultsUrl;
          this.labels = {
            categories: decodeEntities(config.labels?.categories || ''),
            articles: decodeEntities(config.labels?.articles || ''),
            pages: decodeEntities(config.labels?.pages || ''),
            resultsTitle: decodeEntities(config.resultsTitle || ''),
            noResultsLabel: decodeEntities(config.noResultsLabel || ''),
            previousPage: decodeEntities(config.previousPage || ''),
            nextPage: decodeEntities(config.nextPage || ''),
            goToPage: decodeEntities(config.goToPage || '[[page]]'),
          };
        }
      } catch {
        this.labels = {};
      }
    }

    #localizeUrl(url) {
      if (typeof url !== 'string' || url.startsWith('/') === false || url.startsWith('//')) {
        return url;
      }

      const root = this.localeRoot;

      if (root === '' || url === root || url.startsWith(`${root}/`)) {
        return url;
      }

      return `${root}${url}`;
    }

    #assignIndex(index) {
      const categories = Array.isArray(index?.categories) ? index.categories : [];
      const articles = Array.isArray(index?.articles) ? index.articles : [];

      this.index = {
        categories: categories.map((item) => ({
          title: item?.title,
          url: this.#localizeUrl(item?.url),
        })),
        articles: articles.map((item) => ({
          question: item?.question,
          url: this.#localizeUrl(item?.url),
        })),
      };
    }

    async #loadIndex() {
      try {
        this.#assignIndex(await fetchHelpJson(this.fileName));
      } catch {
        if (this.fileName === 'help.json') {
          this.#assignIndex(null);
          return;
        }

        try {
          this.#assignIndex(await fetchHelpJson('help.json'));
        } catch {
          this.#assignIndex(null);
        }
      }
    }

    #applyQueryFromUrl() {
      const query = new URLSearchParams(window.location.search).get('q');

      if (query == null || query.trim() === '') {
        return;
      }

      this.input.value = query;
      this.#renderFullResults(query.trim());
    }

    #search(items, field, query) {
      return items
        .map((item, index) => ({
          item,
          index,
          rank: rankText(item?.[field], query),
        }))
        .filter((entry) => entry.rank < 3 && isSafeUrl(entry.item?.url))
        .sort((left, right) => left.rank - right.rank || left.index - right.index)
        .map((entry) => entry.item);
    }

    #resultsUrlFor(query) {
      const url = new URL(this.resultsUrl, window.location.origin);

      url.searchParams.set('q', query);

      return `${url.pathname}${url.search}`;
    }

    #icon(name) {
      const template = this.root.querySelector(`[data-help-search-icon="${name}"]`);

      if (template == null) {
        return document.createDocumentFragment();
      }

      return template.content.cloneNode(true);
    }

    #pageList(current, total) {
      if (total <= 7) {
        return Array.from({ length: total }, (_, index) => index + 1);
      }

      const pages = [1];
      const start = Math.max(2, current - 1);
      const end = Math.min(total - 1, current + 1);

      if (start > 2) {
        pages.push('…');
      }

      for (let page = start; page <= end; page += 1) {
        pages.push(page);
      }

      if (end < total - 1) {
        pages.push('…');
      }

      pages.push(total);

      return pages;
    }

    #renderTabs(groups) {
      this.tabs.replaceChildren();
      this.tabs.className = '';
      this.tabs.hidden = groups.length === 0;

      if (groups.length === 0) {
        return;
      }

      const scroller = document.createElement('div');
      const scrollerInner = document.createElement('div');
      const list = document.createElement('div');
      const position = document.createElement('span');

      this.tabs.id = 'HelpSearchMainTabs';
      this.tabs.className = 'tabs-nav tabs-nav--center tabs-nav--no-border tabs-nav--edge2edge';
      scroller.className = 'tabs-nav__scroller hide-scrollbar';
      scrollerInner.className = 'tabs-nav__scroller-inner';
      list.className = 'tabs-nav__item-list';
      position.className = 'tabs-nav__position';

      for (const group of groups) {
        const button = document.createElement('button');

        button.type = 'button';
        button.className = 'tabs-nav__item heading heading--small';
        button.setAttribute('aria-expanded', group.type === this.activeType ? 'true' : 'false');
        button.setAttribute('aria-controls', group.panelId);
        button.textContent = `${group.label} (${group.items.length})`;
        button.addEventListener('click', () => {
          if (group.type === this.activeType) {
            return;
          }

          this.activeType = group.type;
          this.currentPage = 1;
          this.#paintResults();
        });
        list.append(button);
      }

      scrollerInner.append(list, position);
      scroller.append(scrollerInner);
      this.tabs.append(scroller);
      requestAnimationFrame(() => {
        this.#placeTabIndicator(this.tabs);
      });
    }

    #placeTabIndicator(nav) {
      const selected = nav.querySelector('[aria-expanded="true"]');
      const position = nav.querySelector('.tabs-nav__position');
      const parent = position?.parentElement;

      if (selected == null || position == null || parent == null || parent.clientWidth === 0) {
        return;
      }

      const scale = selected.clientWidth / parent.clientWidth;
      const translate = selected.offsetLeft / parent.clientWidth / scale;

      position.style.setProperty('--scale', String(scale));
      position.style.setProperty('--translate', `${translate * 100}%`);
      position.classList.add('is-initialized');
    }

    #renderPagination(totalPages) {
      if (totalPages <= 1) {
        return null;
      }

      const pagination = document.createElement('div');
      const nav = document.createElement('nav');
      const pages = this.#pageList(this.currentPage, totalPages);

      pagination.className = 'pagination';
      nav.className = 'pagination__nav';

      if (this.currentPage > 1) {
        nav.append(this.#paginationControl('prev', this.currentPage - 1, this.labels.previousPage));
      }

      for (const page of pages) {
        if (page === '…') {
          const gap = document.createElement('span');

          gap.className = 'pagination__nav-item heading heading--small';
          gap.textContent = '…';
          nav.append(gap);
          continue;
        }

        if (page === this.currentPage) {
          const current = document.createElement('span');

          current.className = 'pagination__nav-item heading heading--small';
          current.setAttribute('aria-current', 'page');
          current.textContent = String(page);
          nav.append(current);
          continue;
        }

        nav.append(this.#paginationControl('page', page, (this.labels.goToPage || '').replace('[[page]]', String(page))));
      }

      if (this.currentPage < totalPages) {
        nav.append(this.#paginationControl('next', this.currentPage + 1, this.labels.nextPage));
      }

      pagination.append(nav);

      return pagination;
    }

    #paginationControl(kind, page, label) {
      const link = document.createElement('a');

      link.className = 'pagination__nav-item';
      link.href = this.#resultsUrlFor(this.input.value.trim());
      link.dataset.page = String(page);
      link.setAttribute('aria-label', label);

      if (kind === 'page') {
        link.classList.add('heading', 'heading--small');
        link.textContent = String(page);
      } else {
        link.append(this.#icon(kind));
      }

      if (kind === 'prev') {
        link.rel = 'prev';
      }

      if (kind === 'next') {
        link.rel = 'next';
      }

      link.addEventListener('click', (event) => {
        event.preventDefault();
        this.currentPage = page;
        this.#paintResults();
        this.results.scrollIntoView({ block: 'nearest' });
      });

      return link;
    }

    #renderResultList(items, field) {
      const list = document.createElement('ul');

      list.className = 'predictive-search__linklist predictive-search__linklist--narrow predictive-search__linklist--bordered list--unstyled';
      list.setAttribute('role', 'list');

      for (const item of items) {
        const itemElement = document.createElement('li');
        const link = document.createElement('a');
        const label = document.createElement('span');

        itemElement.className = 'predictive-search__linklist-item';
        link.className = 'predictive-search__linklist-link';
        link.href = item.url;
        label.textContent = item[field];
        link.append(label, this.#icon('link'));
        itemElement.append(link);
        list.append(itemElement);
      }

      return list;
    }

    #paintResults() {
      const groups = [
        {
          type: 'category',
          label: this.labels.categories,
          field: 'title',
          items: this.matchedCategories,
          panelId: `HelpSearchMainPanel-${this.input.id}-category`,
        },
        {
          type: 'article',
          label: this.labels.pages || this.labels.articles,
          field: 'question',
          items: this.matchedArticles,
          panelId: `HelpSearchMainPanel-${this.input.id}-article`,
        },
      ].filter((group) => group.items.length > 0);

      if (groups.some((group) => group.type === this.activeType) === false) {
        this.activeType = groups[0].type;
        this.currentPage = 1;
      }

      const activeGroup = groups.find((group) => group.type === this.activeType);
      const start = (this.currentPage - 1) * this.pageSize;
      const visibleItems = activeGroup.items.slice(start, start + this.pageSize);
      const totalPages = Math.ceil(activeGroup.items.length / this.pageSize);
      const panel = document.createElement('div');

      this.header.classList.remove('page-header--clear');
      this.#renderTabs(groups);
      panel.id = activeGroup.panelId;
      panel.className = 'main-search__category-result';
      panel.append(this.#renderResultList(visibleItems, activeGroup.field));

      const pagination = this.#renderPagination(totalPages);

      if (pagination != null) {
        panel.append(pagination);
      }

      this.results.replaceChildren(panel);
    }

    #renderFullResults(query) {
      this.results.replaceChildren();
      this.tabs.replaceChildren();
      this.tabs.className = '';
      this.tabs.hidden = true;

      if (query.length < this.minLength) {
        this.header.classList.add('page-header--clear');
        this.heading.textContent = this.heading.dataset.defaultHeading || '';
        return;
      }

      const title = this.labels.resultsTitle || '';

      this.heading.textContent = title.includes('[[terms]]')
        ? title.replace('[[terms]]', query)
        : `${title} "${query}"`;
      this.matchedCategories = this.#search(this.index.categories, 'title', query);
      this.matchedArticles = this.#search(this.index.articles, 'question', query);

      if (this.matchedCategories.length === 0 && this.matchedArticles.length === 0) {
        const empty = document.createElement('div');
        const message = document.createElement('p');

        this.header.classList.add('page-header--clear');
        empty.className = 'empty-state empty-state--bottom-only';
        message.className = 'text--large';
        message.textContent = this.labels.noResultsLabel;
        empty.append(message);
        this.results.append(empty);
        return;
      }

      this.#paintResults();
    }
  }

  const initHelpSearchMain = (scope) => {
    const roots = scope.querySelectorAll('[data-help-search-main]');

    for (const root of roots) {
      if (root.getAttribute('data-help-search-main-ready') === 'true') {
        continue;
      }

      root.setAttribute('data-help-search-main-ready', 'true');
      instances.set(root, new HelpSearchMain(root));
    }
  };

  const destroyHelpSearchMain = (scope) => {
    const roots = scope.matches?.('[data-help-search-main]')
      ? [scope]
      : scope.querySelectorAll('[data-help-search-main]');

    for (const root of roots) {
      const instance = instances.get(root);

      if (instance != null) {
        instance.destroy();
        instances.delete(root);
      }
    }
  };

  if (window.__helpSearchMainBound !== true) {
    window.__helpSearchMainBound = true;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        initHelpSearchMain(document);
      });
    } else {
      initHelpSearchMain(document);
    }

    document.addEventListener('shopify:section:load', (event) => {
      if (event.target != null) {
        initHelpSearchMain(event.target);
      }
    });

    document.addEventListener('shopify:section:unload', (event) => {
      if (event.target != null) {
        destroyHelpSearchMain(event.target);
      }
    });
  }
})();
